import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { isEmpty } from 'lodash';

import { AvailabilityContext, RecordAvailability } from '@app/contexts';
import { queryKeys, useFeatureFlag } from '@app/hooks';
import { makeRequest } from '@app/utils';
import { Feature } from '@app/constants';

import { RtacDTO, useProcessedAvailability } from './useProcessedAvailability';

export interface ILLLink {
  link: string;
  label: string;
}

type Availability = Record<string, RecordAvailability>;
type ILLDto = { instanceId: string; links: ILLLink[] }[];

export const AvailabilityProvider: FC = ({ children }) => {
  const [availability, setAvailability] = useState<Availability>({});
  const [ids, setIds] = useState<string[]>([]);
  const [ILLLinks, setILLLinks] = useState<Record<string, ILLLink[]>>({});
  const [isFirstPage, setIsFirstPage] = useState(false);
  const { isActive: isILLFlagActive } = useFeatureFlag({
    name: Feature.ILLIntegrations,
  });

  const getAvailability = useCallback(
    (requestedId: string[], currentPageNumber) => {
      setIds(requestedId);
      setIsFirstPage(currentPageNumber === 1);
    },
    []
  );

  const availabilityQuery = useQuery(
    [queryKeys.availability, ids],
    () =>
      makeRequest<RtacDTO>({
        url: '/opac-rtac/rtac',
        params: { fullPeriodicals: true, instanceIds: ids.join(',') },
      }),
    { enabled: !isEmpty(ids) }
  );

  const { processAvailability } = useProcessedAvailability({
    availabilityQuery,
    ids,
  });

  useEffect(() => {
    if (availabilityQuery.isFetched) {
      const processedAvailability = processAvailability(availabilityQuery.data);

      setAvailability(prevState =>
        isFirstPage
          ? processedAvailability
          : { ...prevState, ...processedAvailability }
      );
    }
  }, [
    availabilityQuery.data,
    availabilityQuery.isFetched,
    isFirstPage,
    processAvailability,
  ]);

  const ILLQuery = useQuery(
    [queryKeys.ILLLinks, ids],
    () =>
      makeRequest<ILLDto>({
        url: '/opac-tools/ill-links',
        method: 'POST',
        data: { instanceIds: ids },
      }).then(data => {
        const newILLLinks = {};

        data.forEach(
          ({ instanceId, links }) => (newILLLinks[instanceId] = links)
        );

        setILLLinks(prevState => ({ ...prevState, ...newILLLinks }));

        return data;
      }),
    { enabled: isILLFlagActive && !isEmpty(ids) }
  );

  const value = useMemo(
    () => ({
      getAvailability,
      availability,
      ILLLinks,
      isAvailabilityLoading:
        availabilityQuery.isFetching || ILLQuery.isFetching,
    }),
    [
      getAvailability,
      availability,
      ILLLinks,
      availabilityQuery.isFetching,
      ILLQuery.isFetching,
    ]
  );

  return (
    <AvailabilityContext.Provider value={value}>
      {children}
    </AvailabilityContext.Provider>
  );
};
